import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Button, { ButtonTypes } from 'components/common/button';
import ClearExternalCustomerSearchResults from 'actions/external_customer_lookup/clear_external_customer_search_results';
import connect from 'components/lib/connect';
import CustomerProfile from 'models/customer_profile';
import Err from 'models/err';
import ExternalCustomerLookupSearchResults from './external_customer_lookup_search_results';
import ExternalCustomerProfile from 'models/external_customer_profile';
import { ExternalLookupResultAttributeDef, ExternalLookupSearchFieldDef } from 'models/customer_profile_def';
import Input from 'components/common/input';
import LinkExternalCustomer from 'actions/external_customer_lookup/link_external_customer';
import NavigateBack from 'actions/current_location/navigate_back';
import SearchExternalCustomers from 'actions/external_customer_lookup/search_external_customers';
import Tooltip, { FocusManager } from 'components/common/tooltip';

export class ExternalCustomerLookupSearch extends React.Component {
  constructor(props) {
    super(props);
    _.bindAll(this, [
      'handleChangeField',
      'handleClickClear',
      'handleClickSearch',
      'handleFieldKeyDown',
      'handleLinkAndConfirm',
    ]);

    this.state = {
      ...this.getInitialSearchFieldState(props),
      hideErrorsForField: {},
      profileToConfirm: undefined,
    };
  }

  render() {
    return (
      <div className="externalCustomerLookup">
        {this.renderSearchPanel()}
        <ExternalCustomerLookupSearchResults
          externalSystemName={this.props.externalSystemName}
          isAlreadyLinked={this.props.isAlreadyLinked}
          isExternalLookupPending={this.props.isExternalLookupPending}
          lookupResultAttributes={this.props.lookupResultAttributes}
          lookupResultProfiles={this.props.lookupResultProfiles}
          lookupSuggestions={this.props.lookupSuggestions}
          onCancel={this.props.onCancel}
          onLink={this.handleLinkAndConfirm}
          profile={this.props.profile}
        />
      </div>
    );
  }

  renderSearchPanel() {
    if (!this.props.lookupSearchFields.length) {
      return null;
    }

    return (
      <div className="externalCustomerLookup-search">
        {this.renderSearchFields()}
        {this.renderSearchFooter()}
      </div>
    );
  }

  renderSearchFields() {
    const afterSearchText = this.props.externalSystemName ? ` in ${this.props.externalSystemName}` : '';
    return (
      <div className="externalCustomerLookup-searchFields">
        <div className="externalCustomerLookup-searchFields-header">
          Search
          {afterSearchText}
        </div>
        {this.renderSearchError()}
        {_.map(this.props.lookupSearchFields, (searchField, index) => this.renderSearchField(searchField, index))}
      </div>
    );
  }

  renderSearchError() {
    const err = _.find(this.props.errors, e => !e.attr);
    if (!err) {
      return null;
    }

    let errorMessage;
    let featureName = 'search';
    if (this.props.externalSystemName) {
      featureName = `${this.props.externalSystemName} search`;
    }

    if (err.code === Err.Code.OPERATION_FAILED) {
      errorMessage = `Sorry, ${featureName} is not working right now. Try again soon.`;
    } else if (err.code === Err.Code.INVALID_STATE) {
      errorMessage = `Sorry, ${featureName} is no longer available. Please talk to your supervisor.`;
    } else {
      // these will likely be for if (err.code === Err.Code.UNEXPECTED_ERROR || err.code === Err.Code.INVALID)
      // which would correpond to circuit breaker errors, SN 5xx errors, or lookup 4xx errors
      errorMessage = `Sorry, ${featureName} is not working right now.`;
    }

    return <div className="externalCustomerLookup-searchFields-error">{errorMessage}</div>;
  }

  renderSearchField(field, index) {
    const errorMessage = this.getErrorMessageForField(field.attr);
    const inputClassNames = classnames('externalCustomerLookup-searchFields-field-input', {
      'externalCustomerLookup-searchFields-field-input-error': !!errorMessage,
    });
    return (
      <div key={field.attr} className="externalCustomerLookup-searchFields-field">
        <label className="externalCustomerLookup-searchFields-field-label">{field.label}</label>
        <Tooltip message={errorMessage} position="right">
          <FocusManager>
            {({ onBlur, onFocus }) => (
              <Input
                autoFocus={index === 0}
                className={inputClassNames}
                onBlur={onBlur}
                onChange={this.handleChangeField}
                onFocus={onFocus}
                onKeyDown={this.handleFieldKeyDown}
                placeholder={field.placeholder || field.label}
                name={field.attr}
                value={this.state[field.attr]}
              />
            )}
          </FocusManager>
        </Tooltip>
      </div>
    );
  }

  renderSearchFooter() {
    return (
      <div className="externalCustomerLookup-searchFields-footer">
        <Button
          blurOnClick
          buttonType={ButtonTypes.TEXT}
          className="externalCustomerLookup-searchFields-footer-clearButton"
          onClick={this.handleClickClear}
        >
          Clear
        </Button>
        <Button
          blurOnClick
          buttonType={ButtonTypes.PRIMARY}
          className="externalCustomerLookup-searchFields-footer-searchButton"
          onClick={this.handleClickSearch}
        >
          Search
        </Button>
      </div>
    );
  }

  handleChangeField(evt) {
    const field = evt.target.name;
    const value = evt.target.value;

    this.setState(({ hideErrorsForField }) => ({
      hideErrorsForField: {
        ...hideErrorsForField,
        [field]: true,
      },
      [field]: value,
    }));
  }

  handleClickClear() {
    this.setState({
      ...this.getInitialSearchFieldState(),
    });
    this.props.onClearSearch();
  }

  handleClickSearch() {
    this.props.onSearchExternalCustomers(
      _.pick(
        this.state,
        _.map(this.props.lookupSearchFields, field => field.attr)
      )
    );
    this.setState({ hideErrorsForField: {} });
  }

  handleFieldKeyDown(evt) {
    if (evt.key === 'Enter') {
      this.handleClickSearch();
    }
  }

  handleLinkAndConfirm(profile, selectionRank) {
    this.setState({ profileToConfirm: profile });
    this.props.onConfirm(profile, this.props.profile.id, selectionRank);
  }

  getErrorMessageForField(field) {
    const err = _.find(this.props.errors, e => e.attr === field);
    if (!err || this.state.hideErrorsForField[field]) {
      return '';
    }

    return err.detail || getMessageForError(err);
  }

  getInitialSearchFieldState(props = this.props) {
    const searchFields = props.lookupSearchFields;
    return _.zipObject(
      _.map(searchFields, field => field.attr),
      _.times(searchFields.length, i => '')
    );
  }
}

function getMessageForError(err) {
  switch (err.code) {
    case Err.Code.BLANK:
      return 'This field cannot be blank';
    default:
      return 'This field is invalid';
  }
}

ExternalCustomerLookupSearch.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.instanceOf(Err)).isRequired,
  externalSystemName: PropTypes.string,
  isAlreadyLinked: PropTypes.bool.isRequired,
  isExternalLookupPending: PropTypes.bool.isRequired,
  lookupResultAttributes: PropTypes.arrayOf(PropTypes.instanceOf(ExternalLookupResultAttributeDef)).isRequired,
  lookupResultProfiles: PropTypes.arrayOf(PropTypes.instanceOf(ExternalCustomerProfile)),
  lookupSearchFields: PropTypes.arrayOf(PropTypes.instanceOf(ExternalLookupSearchFieldDef)).isRequired,
  lookupSuggestions: PropTypes.arrayOf(PropTypes.instanceOf(ExternalCustomerProfile)).isRequired,
  onCancel: PropTypes.func.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onSearchExternalCustomers: PropTypes.func.isRequired,
  profile: PropTypes.instanceOf(CustomerProfile).isRequired,
};

const ExternalCustomerLookupSearchContainer = connect(mapStateToProps, mapExecuteToProps)(ExternalCustomerLookupSearch);

ExternalCustomerLookupSearchContainer.propTypes = {
  profile: PropTypes.instanceOf(CustomerProfile).isRequired,
};

export default ExternalCustomerLookupSearchContainer;

const DEFAULT_CARD_ATTRIBUTES = ['name', 'email', 'phone', 'address'];

function mapStateToProps({ getProvider, isFeatureEnabled }, { profile }) {
  const searchResults = getProvider('externalCustomerSearchResults').get();
  const suggestions = getProvider('externalLookupSuggestions').get();
  const customerProfileDef = getProvider('customerProfileDef').get();

  let lookupResultAttributes = _.get(customerProfileDef, 'externalLookupResultAttributes');
  const cardAttributes = _.get(customerProfileDef, 'cardAttributes');
  if (!lookupResultAttributes && cardAttributes) {
    lookupResultAttributes = createLookupResultAttributeDefs(customerProfileDef.cardAttributes);
  } else if (!lookupResultAttributes) {
    lookupResultAttributes = createLookupResultAttributeDefs(DEFAULT_CARD_ATTRIBUTES);
  }
  const isAlreadyLinked = !!_.get(profile, 'externalCustomerId') || !_.isEmpty(_.get(profile, 'externalCustomerIds'));

  return {
    errors: getProvider('externalCustomerSearchResults').getErrors(),
    externalSystemName: _.get(getProvider('externalCustomerLookupStatus').get(), 'name'),
    isAlreadyLinked,
    isExternalLookupPending: !!getProvider('externalCustomerSearchResults').getPending(),
    lookupResultAttributes,
    lookupResultProfiles: (searchResults && (searchResults.getResults() || [])) || undefined,
    lookupSearchFields: (customerProfileDef && customerProfileDef.externalLookupSearchFields) || [],
    lookupSuggestions: (suggestions && suggestions.getResults()) || [],
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onCancel: () => executeAction(NavigateBack),
    onConfirm: (externalProfile, customerId, selectionRank) =>
      executeAction(LinkExternalCustomer, { externalProfile, customerId, selectionRank }),
    onClearSearch: () => executeAction(ClearExternalCustomerSearchResults),
    onSearchExternalCustomers: query => executeAction(SearchExternalCustomers, query),
  };
}

function createLookupResultAttributeDefs(attrs) {
  return attrs.map(
    attr =>
      new ExternalLookupResultAttributeDef({
        attr,
        label: _.capitalize(attr),
      })
  );
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/external_customer_lookup/external_customer_lookup_search.jsx